/*
 * 360° product spin (2026-09-25)
 * ---------------------------------------------------------------
 * Loaded ONLY for the wiper template (`assets.liquid`, guarded by
 * `template contains "product.stargatoare"`).
 *
 * ⚠️ WHY ITS OWN FILE, not a branch inside v2_product_gallery.js:
 * that gallery already treats a pointer drag as "go to the next slide"
 * (touchstart/touchmove/touchend on the viewer). A spin needs the SAME gesture to
 * mean "rotate" — on ONE slide. Teaching the shared gallery that distinction
 * would change the gesture on every other product template that uses it, which is
 * exactly what must not happen. Here the shared JS is untouched: this file only
 * ever looks at `[data-rotate]`, and it stops the event from reaching the slider
 * so a rotate-drag cannot also flip the slide.
 *
 * The frames are plain <img> urls supplied by the server (the `custom.rotate_frames`
 * metafield, rendered into `data-frames` by
 * snippets/v2_product_gallery.liquid, `when 'rotate'`). The first frame is already
 * in the DOM, so with JS off the slide degrades to a normal image.
 *
 * ⚠️ THE SLIDER IS PAUSED WHILE THE PRODUCT IS BEING TURNED, exactly as it is
 * while a video plays (`_autoPlaySlideVideo` clears the slide timer for the
 * video's duration). Without it the autoplay timer can advance to the next photo
 * mid-drag, and the customer watches the spin disappear. Uses the gallery's
 * PUBLIC pause()/play(), and only resumes a pause it started itself — a gallery
 * the customer paused stays paused.
 */
(function () {
  'use strict';

  var FRAME_WIDTH = 1200; // the frames' natural size; the CDN serves them square

  /**
   * The ProductGallery instance driving this slide, if there is one.
   * `v2_product_gallery.js` assigns `el.pgInstance` on the `.pg-gallery` wrapper.
   */
  function galleryFor(el) {
    var wrap = el.closest('.pg-gallery');
    return wrap && wrap.pgInstance ? wrap.pgInstance : null;
  }

  function initSpin(img) {
    var root = img.parentElement;
    if (!root) return;

    var frames = (img.getAttribute('data-frames') || '')
      .split('|')
      .map(function (u) { return u.trim(); })
      .filter(Boolean);

    if (frames.length < 2) return;

    var index = 0;
    var loaded = {};

    // Preload so a drag does not stutter: the browser has nothing to fetch the
    // first time a frame is shown otherwise.
    function preload() {
      frames.forEach(function (url) {
        if (loaded[url]) return;
        loaded[url] = true;
        var i = new Image();
        i.src = url;
      });
    }

    function show(next) {
      // Wraps, so dragging past the last frame continues from the first — that is
      // what makes a spin feel continuous rather than like a slideshow.
      index = ((next % frames.length) + frames.length) % frames.length;
      img.setAttribute('src', frames[index]);
    }

    // Frame 0 is already rendered by the server; make sure the src is the real
    // url rather than the placeholder the markup ships (the lazy-load pattern the
    // rest of the gallery uses).
    show(0);

    // Bind by walking the slide, not by a class on a container: the controls are
    // SIBLINGS of the frame, and there is deliberately no wrapper element.
    root.querySelectorAll('[data-rotate-step]').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        show(index + Number(btn.getAttribute('data-rotate-step')));
      });
    });

    // ── Drag to spin ────────────────────────────────────────────────────────
    var dragging = false;
    var startX = 0;
    var startIndex = 0;
    // True only when WE paused the slider, so a gallery the customer paused (or
    // that was never autoplaying) is not un-paused by a spin.
    var pausedBySpin = false;
    // One frame per this many pixels. 24 frames × 14px ≈ 336px for a full turn,
    // which roughly matches the width of the image on desktop — so a turn takes
    // about one drag across the image, the convention for a 360.
    var PX_PER_FRAME = 14;

    function onDown(e) {
      dragging = true;
      startX = e.clientX;
      startIndex = index;
      img.classList.add('is-dragging');

      // ⚠️ STOP the event here. Without this the gallery's own touch/pointer
      // handling sees the same drag and flips to the next slide mid-spin.
      e.stopPropagation();

      // Hold the slider still for the duration of the interaction — the same
      // thing the gallery does while a video plays.
      var gallery = galleryFor(root);
      if (gallery && typeof gallery.pause === 'function' && !gallery.autoplayPaused) {
        gallery.pause();
        pausedBySpin = true;
      }

      preload();
    }

    function onMove(e) {
      if (!dragging) return;
      e.preventDefault();
      e.stopPropagation();
      var delta = e.clientX - startX;
      show(startIndex - Math.round(delta / PX_PER_FRAME));
    }

    function onUp(e) {
      if (!dragging) return;
      dragging = false;
      img.classList.remove('is-dragging');
      e.stopPropagation();

      // Hand the slider back, but only if this interaction is what stopped it.
      if (pausedBySpin) {
        pausedBySpin = false;
        var gallery = galleryFor(root);
        if (gallery && typeof gallery.play === 'function') gallery.play();
      }
    }

    root.addEventListener('pointerdown', onDown);
    root.addEventListener('pointermove', onMove);
    root.addEventListener('pointerup', onUp);
    root.addEventListener('pointercancel', onUp);
    root.addEventListener('pointerleave', onUp);

    // Touch, in case the browser has no Pointer Events (older iOS Safari):
    // without this a swipe would scroll the page instead of turning the product.
    root.addEventListener('touchstart', function (e) {
      if (e.touches.length !== 1) return;
      onDown(e.touches[0]);
    }, { passive: true });

    root.addEventListener('touchmove', function (e) {
      if (!dragging || e.touches.length !== 1) return;
      onMove(e.touches[0]);
    }, { passive: false });

    root.addEventListener('touchend', onUp);

    root.classList.add('is-ready');
  }

  function boot() {
    document.querySelectorAll('img[data-rotate]').forEach(initSpin);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
